/**
 * Implémentation Supabase de `SuggestGateway`.
 *
 * Comme pour l'import, aucune règle métier ici : le classement et les poids
 * vivent dans `suggest.ts`. Ce fichier traduit chaque piste en requête.
 *
 * ## La recherche par nom se fait en deux passes
 *
 * La base contient 2 939 enfants, 3 296 personnes autorisées et 2 386 comptes.
 * Les charger pour filtrer côté navigateur serait absurde ; demander à
 * PostgreSQL « quels enfants sont nommés dans ce texte » n'est pas exprimable.
 *
 * D'où deux passes : on extrait les jetons du texte — mots de trois lettres ou
 * plus, sans accents — on demande à la base les personnes dont le nom ou le
 * prénom contient l'un de ces jetons, puis on **vérifie côté code** que tous
 * les jetons du nom sont bien présents. La base dégrossit, `nameAppearsIn`
 * tranche.
 *
 * Sans la seconde passe, « Charlotte » seul remonterait dix familles.
 */

import { db } from './db';
import { nameAppearsIn, nameTokens, type FamilyHit, type OpenInvoice, type SuggestGateway } from './suggest';

/** Au-delà, la requête `or` devient déraisonnable et le texte peu fiable. */
const MAX_JETONS = 6;

function jetons(texte: string): string[] {
  return [...nameTokens(texte)].slice(0, MAX_JETONS);
}

/** `Nom Prénom`, en évitant les espaces parasites quand l'un manque. */
function etiquette(nom?: string | null, prenom?: string | null): string {
  return [nom, prenom].filter(Boolean).join(' ').trim() || 'Compte sans nom';
}

interface LigneUser { id: string; nom: string | null; prenom: string | null }

export const suggestGatewaySupabase: SuggestGateway = {
  async familyByConfirmedIban(iban) {
    const { data, error } = await db
      .from('payment_counterparties')
      // La clé étrangère est NOMMÉE, et ce n'est pas un détail de style.
      // `payment_counterparties` pointe DEUX fois vers `users` : `user_id`, la
      // famille, et `confirmed_by`, l'administrateur qui a validé. PostgREST
      // refuse alors `users(...)` comme ambigu (PGRST201) et la requête échoue.
      //
      // C'est le défaut qui a rendu l'écran inutilisable au premier essai :
      // cette piste est la première appelée dès qu'un IBAN est connu, donc
      // toutes les suggestions tombaient, sur tous les paiements.
      .select('user_id, display_name, confirmed_at, users!payment_counterparties_user_id_fkey(nom, prenom)')
      .eq('iban', iban)
      // Seules les associations validées par un humain comptent comme piste
      // forte. Une simple observation n'est pas une confirmation.
      .not('confirmed_at', 'is', null)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const u = (data as unknown as { users: LigneUser }).users;
    return {
      userId: data.user_id,
      label: data.display_name ?? etiquette(u?.nom, u?.prenom),
      source: 'iban_confirmed',
      matched: iban,
    };
  },

  /**
   * Les familles déjà servies par ce compte.
   *
   * C'est la démarche « je regarde dans la compta d'autres versements arrivés de
   * ce numéro de compte ». On remonte du paiement à son allocation, puis à la
   * facture, puis à la famille.
   */
  async familiesByIbanHistory(iban) {
    const { data, error } = await db
      .from('payments')
      .select(`
        counterparty_name,
        payment_allocations!inner(
          invoices!inner(user_id, users!inner(nom, prenom))
        )
      `)
      .eq('counterparty_iban', iban)
      .limit(20);
    if (error) throw error;

    const parFamille = new Map<string, FamilyHit>();
    for (const p of (data ?? []) as unknown as {
      counterparty_name: string | null;
      payment_allocations: { invoices: { user_id: string; users: LigneUser } }[];
    }[]) {
      for (const a of p.payment_allocations ?? []) {
        const inv = a.invoices;
        if (!inv?.user_id || parFamille.has(inv.user_id)) continue;
        parFamille.set(inv.user_id, {
          userId: inv.user_id,
          label: etiquette(inv.users?.nom, inv.users?.prenom),
          source: 'iban_history',
          matched: p.counterparty_name ?? iban,
        });
      }
    }
    return [...parFamille.values()];
  },

  async familiesByKidName(communication) {
    const mots = jetons(communication);
    if (mots.length === 0) return [];

    const { data, error } = await db
      .from('kids')
      .select('nom, prenom, user_id, users!inner(nom, prenom)')
      .or(mots.flatMap((m) => [`nom.ilike.*${m}*`, `prenom.ilike.*${m}*`]).join(','))
      .limit(200);
    if (error) throw error;

    const parFamille = new Map<string, FamilyHit>();
    for (const k of (data ?? []) as unknown as {
      nom: string; prenom: string; user_id: string; users: LigneUser;
    }[]) {
      // Seconde passe : la base a dégrossi sur un jeton, on exige le nom entier.
      if (!nameAppearsIn(`${k.nom} ${k.prenom}`, communication)) continue;
      if (parFamille.has(k.user_id)) continue;
      parFamille.set(k.user_id, {
        userId: k.user_id,
        label: etiquette(k.users?.nom, k.users?.prenom),
        source: 'kid_name',
        matched: `${k.prenom} ${k.nom}`,
      });
    }
    return [...parFamille.values()];
  },

  async familiesByAuthorizedPerson(name) {
    const mots = jetons(name);
    if (mots.length === 0) return [];

    const { data, error } = await db
      .from('authorized_persons')
      .select('first_name, last_name, relationship, user_id, users!inner(nom, prenom)')
      .or(mots.flatMap((m) => [`first_name.ilike.*${m}*`, `last_name.ilike.*${m}*`]).join(','))
      .limit(200);
    if (error) throw error;

    const parFamille = new Map<string, FamilyHit>();
    for (const p of (data ?? []) as unknown as {
      first_name: string; last_name: string; relationship: string | null;
      user_id: string; users: LigneUser;
    }[]) {
      if (!nameAppearsIn(`${p.last_name} ${p.first_name}`, name)) continue;
      if (parFamille.has(p.user_id)) continue;
      parFamille.set(p.user_id, {
        userId: p.user_id,
        label: etiquette(p.users?.nom, p.users?.prenom),
        source: 'authorized_person',
        matched: `${p.first_name} ${p.last_name}${p.relationship ? ` — ${p.relationship}` : ''}`,
      });
    }
    return [...parFamille.values()];
  },

  async familiesByAccountHolder(name) {
    const mots = jetons(name);
    if (mots.length === 0) return [];

    const { data, error } = await db
      .from('users')
      .select('id, nom, prenom')
      .or(mots.flatMap((m) => [`nom.ilike.*${m}*`, `prenom.ilike.*${m}*`]).join(','))
      .limit(200);
    if (error) throw error;

    const hits: FamilyHit[] = [];
    for (const u of (data ?? []) as LigneUser[]) {
      if (!nameAppearsIn(etiquette(u.nom, u.prenom), name)) continue;
      hits.push({
        userId: u.id,
        label: etiquette(u.nom, u.prenom),
        source: 'account_holder',
        matched: name,
      });
    }
    return hits;
  },

  /**
   * Les factures encore dues, lues dans la vue de solde.
   *
   * `outstanding_cents > 0` et statut non annulé : une facture annulée ne doit
   * jamais être proposée, même si son solde paraît ouvert.
   */
  async openInvoicesFor(userIds) {
    const parFamille = new Map<string, OpenInvoice[]>(userIds.map((id) => [id, []]));
    if (userIds.length === 0) return parFamille;

    const { data, error } = await db
      .from('v_invoice_balances')
      .select('invoice_id, invoice_number, user_id, outstanding_cents, due_date, settlement, pdf_url')
      .in('user_id', userIds)
      .gt('outstanding_cents', 0)
      .neq('settlement', 'cancelled')
      .order('due_date', { ascending: true });
    if (error) throw error;

    for (const l of (data ?? []) as unknown as {
      invoice_id: string; invoice_number: string; user_id: string;
      outstanding_cents: number; due_date: string | null; pdf_url: string | null;
    }[]) {
      parFamille.get(l.user_id)?.push({
        invoiceId: l.invoice_id,
        invoiceNumber: l.invoice_number,
        outstandingCents: l.outstanding_cents,
        dueDate: l.due_date,
        pdfUrl: l.pdf_url,
      });
    }
    return parFamille;
  },
};
